/* eslint-disable array-callback-return */
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components/macro';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup';
import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Paper as MuiPaper,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Button,
  Checkbox,
  Snackbar,
  Alert,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import { spacing } from '@mui/system';
import axios from '../../CustomAxios';
import { Formik } from 'formik';
import Loading from '../components/Loading';
import { DateTimePicker } from '@mui/lab';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

const Card = styled(MuiCard)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function createData(id, job_id, name, delivery, required_date, received_date, data) {
  return { id, job_id, name, delivery, required_date, received_date, data };
}

function FormDetails({ details, id, availableJobList }) {
  const [down, setDown] = useState({});
  const [selectedJobs, setSelectedJobs] = useState(() => {
    const selected = {};
    details?.jobs?.forEach(job => {
      selected[job._id] = true;
    });
    return selected;
  });
  const [deliveryCharges, setDeliveryCharges] = useState(() => {
    const charges = {};
    details?.job_delivery_charges?.forEach(item => {
      charges[item.job] = item.delivery_charge;
    });
    // Also ensure jobs already in the challan have a charge (default 0 if missing)
    details?.jobs?.forEach(job => {
      if (charges[job._id] === undefined) charges[job._id] = 0;
    });
    return charges;
  });
  const [currentChallanDate, setCurrentChallanDate] = useState(new Date(details?.challan_date));
  const [is_left_for_delivery, setLeft] = useState(details?.is_left_for_delivery || false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(false);
  const [loader, setLoader] = useState(false);
  const snackRef = useRef(null);
  const navigate = useNavigate();
  const [state, setState] = useState({
    open: false,
    vertical: 'top',
    horizontal: 'center',
  });
  const { vertical, horizontal, open } = state;
  const handleClick = (newState) => () => {
    setState({ open: true, ...newState });
  };
  const handleClose = () => {
    setState({ ...state, open: false });
  };

  // Combine current jobs with available jobs for display
  const jobTableRows = () => {
    let rows = [];
    details?.jobs?.forEach((row) => {
      rows.push(
        createData(
          row._id,
          row.job_id,
          row.name,
          `${row.delivery?.address_line_one}, ${row.delivery?.address_line_two}, ${row.delivery?.city}, ${row.delivery?.state}, ${row.delivery?.pincode}, India`,
          row.required_date,
          row.received_date,
          row
        )
      );
    });
    availableJobList.forEach((row) => {
      rows.push(
        createData(
          row._id,
          row.job_id,
          row.name,
          `${row.delivery?.address_line_one}, ${row.delivery?.address_line_two}, ${row.delivery?.city}, ${row.delivery?.state}, ${row.delivery?.pincode}, India`,
          row.required_date,
          row.received_date,
          row
        )
      );
    });
    return rows;
  };

  const submit = async (values, id) => {
    try {
      const jobs = Object.keys(selectedJobs).filter(jobId => selectedJobs[jobId] === true);
      if (!jobs.length) {
        setErr('Select at least one job');
        snackRef.current.click();
        return;
      }

      // Build delivery charges array for selected jobs
      const jobDeliveryCharges = jobs.map(jobId => ({
        job: jobId,
        delivery_charge: deliveryCharges[jobId] !== undefined ? deliveryCharges[jobId] : 0
      }));

      const invalidCharge = jobDeliveryCharges.find(c => c.delivery_charge < 0);
      if (invalidCharge) {
        setErr('Delivery charge cannot be negative');
        snackRef.current.click();
        return;
      }

      setLoader(true);
      const userData = JSON.parse(localStorage.getItem('AuthData'));
      const config = {
        headers: {
          Authorization: 'Bearer ' + userData.access_token,
        },
      };
      const { data } = await axios.put(
        `/api/v1/${userData.type}/challan/${id}`,
        {
          name: values.name,
          description: values.description,
          is_left_for_delivery: is_left_for_delivery,
          challan_date: new Date(values.challan_date).toISOString(),
          jobs: jobs,
          job_delivery_charges: jobDeliveryCharges,
        },
        config
      );
      if (data.success) {
        setLoader(false);
        setSuccess(true);
        setErr('');
        snackRef.current.click();
        navigate('/dashboard/challan/', {
          state: {
            success: true,
            message: 'Challan Details Updated Successfully',
          },
        });
      }
    } catch (e) {
      setLoader(false);
      if (e.response?.data?.error) {
        setErr(e.response.data.error);
      } else {
        setErr('Server Error');
      }
      snackRef.current.click();
    }
  };

  function showErrorAlert(message) {
    setErr(message);
    snackRef.current.click();
  }

  function checkSelectedDateIsInBetween(selectedDateInput) {
    const jobCreationDates = [];
    Object.keys(selectedJobs).forEach((job_item) => {
      if (selectedJobs[job_item]) {
        const allRows = jobTableRows();
        const currentJobItem = allRows.find((it) => it.id === job_item);
        if (currentJobItem) {
          jobCreationDates.push(new Date(currentJobItem?.data?.received_date));
        }
      }
    });
    if (jobCreationDates.length === 0) return true; // no jobs selected, skip validation
    jobCreationDates.sort();

    const dateCheck = new Date(selectedDateInput);
    const dateFrom = new Date(jobCreationDates[0]);
    const latestChallanDate = new Date(jobCreationDates[jobCreationDates.length - 1]);
    const dateTo = new Date(latestChallanDate.setFullYear(latestChallanDate.getFullYear() + 1));

    return dateCheck.getTime() <= dateTo.getTime() && dateCheck.getTime() >= dateFrom.getTime();
  }

  function processDateFormat(dateInput) {
    if (!dateInput) return '';
    var month = ('0' + parseInt(dateInput.getMonth() + 1)).slice(-2);
    var day = ('0' + parseInt(dateInput.getDate())).slice(-2);
    var hour = parseInt(dateInput.getHours());
    var minute = parseInt(dateInput.getMinutes());
    return dateInput.getFullYear() + '-' + month + '-' + day + ':' + hour + ':' + minute;
  }

  return (
    <Formik
      initialValues={{
        name: details?.name || '',
        description: details?.description || '',
        challan_id: details?.challan_id || '',
        customer_company: details?.customer_company || '',
        submit: false,
        challan_date: processDateFormat(new Date(details?.challan_date)),
      }}
      validationSchema={Yup.object().shape({
        name: Yup.string().required('Challan Name is required'),
        description: Yup.string().required('Description is required'),
        challan_date: Yup.string().required('Challan Date is required'),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await submit(values, id);
        } catch (error) {
          const message = error.message || 'Something went wrong';
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        setFieldValue,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <form noValidate onSubmit={handleSubmit} autoComplete='off'>
          <Button
            ref={snackRef}
            style={{ display: 'none' }}
            onClick={handleClick({ vertical: 'top', horizontal: 'center' })}
          >
            Click
          </Button>
          <Snackbar
            anchorOrigin={{ vertical, horizontal }}
            open={open}
            onClose={handleClose}
            key={vertical + horizontal}
          >
            {err ? (
              <Alert mt={2} mb={4} severity='error'>
                {err}
              </Alert>
            ) : success ? (
              <Alert mt={2} mb={4} severity='success'>
                Challan Updated Successfully
              </Alert>
            ) : null}
          </Snackbar>
          {!loader ? (
            <Card mb={6}>
              <CardContent>
                <Paper elevation={3} style={{ padding: '2%' }}>
                  <Grid container spacing={5}>
                    <Grid item xs={12} lg={12} md={12}>
                      <Typography variant='h6' gutterBottom>
                        Update Challan
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={12} lg={6}>
                      <TextField
                        type='text'
                        fullWidth
                        label='Challan ID'
                        value={values?.challan_id}
                        inputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={6}>
                      <TextField
                        fullWidth
                        name='name'
                        value={values.name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.name && errors.name)}
                        label='Challan Name'
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={6}>
                      <TextField
                        type='text'
                        fullWidth
                        label='AP Company'
                        value={`${details?.ap_company?.name} [${details?.ap_company?.company_code}]`}
                        inputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={6}>
                      <TextField
                        type='text'
                        fullWidth
                        label='Customer Company'
                        value={values?.customer_company?.name || details?.customer_company?.name}
                        inputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                      <TextField
                        fullWidth
                        name='description'
                        value={values.description}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={Boolean(touched.description && errors.description)}
                        label='Description'
                        helperText={touched.description && errors.description}
                        multiline
                      />
                    </Grid>
                    <Grid item xs={12} md={12} lg={6}>
                      <FormControl fullWidth>
                        <DateTimePicker
                          name='challan_date'
                          renderInput={(props) => (
                            <TextField
                              {...props}
                              error={touched.challan_date && errors.challan_date}
                            />
                          )}
                          label='Challan Date'
                          value={values.challan_date || null}
                          onChange={(newValue) => {
                            if (!checkSelectedDateIsInBetween(newValue)) {
                              showErrorAlert(
                                'Please select a date between the oldest job date and one year from latest job date'
                              );
                              setFieldValue(
                                'challan_date',
                                processDateFormat(new Date(details?.challan_date))
                              );
                              return;
                            }
                            setCurrentChallanDate(new Date(newValue));
                            let selectedDate = newValue;
                            var month = ('0' + parseInt(selectedDate?.getMonth() + 1)).slice(-2);
                            var day = ('0' + parseInt(selectedDate?.getDate())).slice(-2);
                            var hour = parseInt(selectedDate.getHours());
                            var minute = parseInt(selectedDate.getMinutes());
                            let date =
                              selectedDate?.getFullYear() +
                              '-' +
                              month +
                              '-' +
                              day +
                              ':' +
                              hour +
                              ':' +
                              minute;
                            setFieldValue('challan_date', date);
                          }}
                          onBlur={handleBlur}
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} lg={3} md={3}>
                      <FormControl fullWidth component='fieldset' variant='standard'>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={(e) => setLeft(e.target.checked)}
                              checked={is_left_for_delivery}
                            />
                          }
                          label={'LEFT FOR DELIVERY'}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
                <Divider />
                <Paper elevation={3} style={{ padding: '2%' }}>
                  <Grid container spacing={5}>
                    <Grid item xs={12} lg={12} md={12}>
                      <Typography variant='h5' sx={{ mb: 3 }}>
                        Job Details
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                          <TableHead>
                            <TableRow>
                              <TableCell padding='checkbox' />
                              <TableCell padding='checkbox' />
                              <TableCell align='left'>Job Id</TableCell>
                              <TableCell align='left'>Job Name</TableCell>
                              <TableCell align='left'>Delivery Address</TableCell>
                              <TableCell align='left'>Received Date</TableCell>
                              <TableCell align='left'>Delivery Date</TableCell>
                              <TableCell align='left'>Delivery Charge (₹)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {jobTableRows().map((row) => {
                              const isSelected = selectedJobs[row.id] || false;
                              return (
                                <React.Fragment key={row.id}>
                                  <TableRow
                                    sx={{
                                      '&:last-child td, &:last-child th': { border: 0 },
                                      backgroundColor: details?.jobs?.some(j => j._id === row.id)
                                        ? 'aliceblue'
                                        : 'inherit',
                                    }}
                                  >
                                    <TableCell padding='checkbox'>
                                      <Checkbox
                                        color='primary'
                                        checked={isSelected}
                                        name={row.id}
                                        onChange={(e) => {
                                          const currentlySelectedCount = Object.values(selectedJobs).filter(v => v === true).length;
                                          if (!e.target.checked && currentlySelectedCount === 1) {
                                            showErrorAlert('You have to select at least one Job');
                                            return;
                                          }
                                          setSelectedJobs({
                                            ...selectedJobs,
                                            [e.target.name]: e.target.checked,
                                          });
                                          // Initialize delivery charge to 0 if newly selected and not set
                                          if (e.target.checked && deliveryCharges[row.id] === undefined) {
                                            setDeliveryCharges(prev => ({ ...prev, [row.id]: 0 }));
                                          }
                                          if (!checkSelectedDateIsInBetween(currentChallanDate)) {
                                            showErrorAlert('Please change the Challan date');
                                            setFieldValue('challan_date', '');
                                          }
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <IconButton
                                        aria-label='expand row'
                                        size='small'
                                        onClick={() => {
                                          setDown(prev => ({ ...prev, [row.id]: !prev[row.id] }));
                                        }}
                                      >
                                        {down[row.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                      </IconButton>
                                    </TableCell>
                                    <TableCell component='th' scope='row'>
                                      {row.job_id}
                                    </TableCell>
                                    <TableCell align='left'>{row.name}</TableCell>
                                    <TableCell align='left'>{row.delivery}</TableCell>
                                    <TableCell align='left'>
                                      {new Date(row.received_date).toLocaleString()}
                                    </TableCell>
                                    <TableCell align='left'>
                                      {new Date(row.required_date).toLocaleString()}
                                    </TableCell>
                                    <TableCell align='left'>
                                      <TextField
                                        type='number'
                                        size='small'
                                        disabled={!isSelected}
                                        value={deliveryCharges[row.id] ?? ''}
                                        onChange={(e) => {
                                          const val = parseFloat(e.target.value);
                                          setDeliveryCharges({
                                            ...deliveryCharges,
                                            [row.id]: isNaN(val) ? 0 : val,
                                          });
                                        }}
                                        InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                        style={{ width: 100 }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      style={{ paddingBottom: 0, paddingTop: 0 }}
                                      colSpan={8}
                                    >
                                      <Collapse in={down[row.id]} timeout='auto' unmountOnExit>
                                        <Box sx={{ margin: 1, padding: 4 }}>
                                          <Typography variant='h6' gutterBottom component='div'>
                                            Job Plate Details
                                          </Typography>
                                          <Table size='small' aria-label='jobs'>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell align='left'>Size</TableCell>
                                                <TableCell align='left'>Quantity</TableCell>
                                                <TableCell align='left'>Company</TableCell>
                                                <TableCell align='left'>Brand</TableCell>
                                                <TableCell align='left'>Technology</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {row.data.plates.map((plate) => (
                                                <TableRow key={plate.plate_id}>
                                                  <TableCell align='left'>
                                                    {plate?.plate_details?.height}*
                                                    {plate?.plate_details?.width}*
                                                    {plate?.plate_details?.thickness}
                                                  </TableCell>
                                                  <TableCell align='left'>
                                                    {plate?.quantity}
                                                  </TableCell>
                                                  <TableCell align='left'>
                                                    {plate?.plate_details?.company}
                                                  </TableCell>
                                                  <TableCell align='left'>
                                                    {plate?.plate_details?.brand?.name}
                                                  </TableCell>
                                                  <TableCell align='left'>
                                                    {plate?.plate_details?.technology?.name}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Paper>
                <Divider />
                <Paper elevation={3} style={{ padding: '2%' }}>
                  <Grid container spacing={5}>
                    <Grid item xs={12} md={12} lg={12}>
                      <Button
                        type='submit'
                        style={{ textAlign: 'center', margin: '10px' }}
                        mr={2}
                        variant='contained'
                        color='primary'
                        disabled={isSubmitting}
                      >
                        Update Challan
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </CardContent>
            </Card>
          ) : (
            <Loading />
          )}
        </form>
      )}
    </Formik>
  );
}

const getAvailableJobList = async (
  customer_company,
  ap_company,
  setLoader,
  setAvailableJobList
) => {
  try {
    const authData = JSON.parse(localStorage.getItem('AuthData'));
    const config = {
      headers: {
        Authorization: 'Bearer ' + authData.access_token,
      },
    };
    const { data: responseData } = await axios.get(
      `/api/v1/${authData?.type}/jobs?current_status=delivery&customer_company_id=${customer_company}&challan_created=NO&ap_company_id=${ap_company}&populate_plate=YES`,
      config
    );
    if (responseData.success) {
      setAvailableJobList(responseData.data?.data);
    }
    setLoader(false);
  } catch (e) {
    console.log(e);
  }
};

const getChallanDetails = async (id, setLoader, setDetails, setAvailableJobList) => {
  try {
    const authData = JSON.parse(localStorage.getItem('AuthData'));
    const config = {
      headers: {
        Authorization: 'Bearer ' + authData.access_token,
      },
    };
    const { data: responseData } = await axios.get(
      `/api/v1/${authData?.type}/challan/${id}/challan_info`,
      config
    );
    if (responseData.success) {
      setDetails(responseData.data);
      await getAvailableJobList(
        responseData.data?.customer_company._id,
        responseData.data?.ap_company._id,
        setLoader,
        setAvailableJobList
      );
    }
    setLoader(false);
  } catch (e) {
    console.log(e);
    setLoader(false);
  }
};

function UpdateChallan() {
  const { challan_id } = useParams();
  const [loader, setLoader] = useState(true);
  const [details, setDetails] = useState(null);
  const [availableJobList, setAvailableJobList] = useState([]);

  useEffect(() => {
    getChallanDetails(challan_id, setLoader, setDetails, setAvailableJobList);
    // eslint-disable-next-line
  }, []);

  if (!loader && details) {
    return (
      <React.Fragment>
        <Helmet title='Alpha Plus' />
        <Grid justifyContent='space-between' alignItems='center' container spacing={10} mb={4}>
          <Grid item>
            <Typography
              variant='h4'
              gutterBottom
              display='inline'
              sx={{ fontWeight: 600 }}
              color='primary'
            >
              UPDATE CHALLAN
            </Typography>
          </Grid>
          <Grid item>
            <Breadcrumbs aria-label='Breadcrumb' mt={2}>
              <Link component={NavLink} to='/dashboard/tasks'>
                Dashboard
              </Link>
              <Link component={NavLink} to='/dashboard/challan'>
                All Challans
              </Link>
              <Typography>Update Challan</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <FormDetails id={challan_id} details={details} availableJobList={availableJobList} />
          </Grid>
        </Grid>
      </React.Fragment>
    );
  } else {
    return <Loading />;
  }
}

export default UpdateChallan;